import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Loader2, CheckCircle2, Lock, X } from "lucide-react";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function RelazioneEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const debounceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/${id}`);
      setReport(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const isReadOnly = report?.stato === "inviata" && user?.role !== "super_admin";
  const isAuthor = report && (user.role === "super_admin" || report.resident_manager_id === user.id);

  const updateField = (field, value) => {
    if (isReadOnly) return;
    setReport((r) => ({ ...r, [field]: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => autoSave(field, value), 1200);
  };

  const autoSave = async (field, value) => {
    if (isReadOnly || !isAuthor) return;
    setSaving(true);
    try {
      await api.patch(`/reports/${id}`, { [field]: value });
      setSavedAt(new Date());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const invia = async () => {
    try {
      const r = await api.patch(`/reports/${id}/invia`);
      setReport(r.data);
      setShowConfirm(false);
      toast.success("Relazione inviata definitivamente");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;
  if (!report) return <div>Relazione non trovata.</div>;

  return (
    <div className="space-y-6 ih-anim-in max-w-4xl" data-testid="relazione-editor">
      <button onClick={() => navigate("/relazioni")} className="text-sm flex items-center gap-1 text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Tutte le relazioni</button>

      <div className="ih-glass rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{MESI[report.mese - 1]} {report.anno}</div>
            <input
              data-testid="titolo-input"
              type="text"
              value={report.titolo}
              onChange={(e) => updateField("titolo", e.target.value)}
              readOnly={isReadOnly}
              className="font-display text-3xl tracking-tight font-light bg-transparent border-0 outline-0 w-full focus:ring-0 disabled:opacity-100"
            />
          </div>
          <div className="flex items-center gap-2">
            {report.stato === "inviata" ? (
              <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-xs font-semibold flex items-center gap-1.5"><Lock className="h-3 w-3" /> Inviata</span>
            ) : (
              <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold">Bozza</span>
            )}
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4 pb-4 border-b border-border/60">
            {saving ? <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Salvataggio…</span> : savedAt ? <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Salvato {savedAt.toLocaleTimeString("it-IT")}</span> : <span>Modifiche auto-salvate</span>}
          </div>
        )}

        <div className="space-y-5">
          <Section label="Relazione mensile" value={report.relazione_libera} onChange={(v) => updateField("relazione_libera", v)} readOnly={isReadOnly} placeholder="Descrivi l'andamento generale del mese…" rows={6} testid="ta-relazione" />
          <Section label="Criticità osservate" value={report.criticita_osservate} onChange={(v) => updateField("criticita_osservate", v)} readOnly={isReadOnly} placeholder="Problemi, criticità, situazioni da segnalare" rows={4} testid="ta-criticita" />
          <Section label="Miglioramenti effettuati" value={report.miglioramenti_effettuati} onChange={(v) => updateField("miglioramenti_effettuati", v)} readOnly={isReadOnly} placeholder="Azioni positive, miglioramenti realizzati" rows={4} testid="ta-miglioramenti" />
          <Section label="Obiettivi mese successivo" value={report.obiettivi_mese_successivo} onChange={(v) => updateField("obiettivi_mese_successivo", v)} readOnly={isReadOnly} placeholder="Cosa vuoi raggiungere il mese prossimo" rows={4} testid="ta-obiettivi" />
          <Section label="Note (opzionale)" value={report.note} onChange={(v) => updateField("note", v)} readOnly={isReadOnly} placeholder="Note aggiuntive" rows={2} testid="ta-note" />
        </div>

        {report.firma && (
          <div className="mt-6 pt-4 border-t border-border/60 rounded-lg bg-accent/30 p-4 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Firma digitale</div>
            <div>{report.firma.nome_cognome}</div>
            <div className="text-xs text-muted-foreground">{new Date(report.firma.timestamp).toLocaleString("it-IT")}</div>
          </div>
        )}

        {!isReadOnly && isAuthor && (
          <div className="mt-6 flex items-center justify-end gap-3 sticky bottom-3 bg-background/70 backdrop-blur rounded-full p-2 border border-border/60 w-fit ml-auto">
            <button onClick={() => autoSave("titolo", report.titolo)} className="px-4 py-2 rounded-full border border-border text-sm font-medium flex items-center gap-2"><Save className="h-4 w-4" /> Salva bozza</button>
            <button onClick={() => setShowConfirm(true)} data-testid="invia-btn" className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2"><Send className="h-4 w-4" /> Invia definitivamente</button>
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-background rounded-2xl border border-border w-full max-w-md p-6" data-testid="invia-confirm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl">Conferma invio</h2>
              <button onClick={() => setShowConfirm(false)} className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Una volta inviata la relazione <strong>non potrà più essere modificata</strong>. Apporrai la tua firma digitale con timestamp. Procedere?</p>
            <div className="flex gap-2">
              <button onClick={() => setShowConfirm(false)} className="flex-1 px-4 py-2 rounded-full border border-border text-sm">Annulla</button>
              <button onClick={invia} data-testid="invia-confirm-btn" className="flex-1 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">Conferma invio</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, value, onChange, readOnly, placeholder, rows, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`mt-1 w-full rounded-xl border border-border/60 bg-background/50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${readOnly ? "opacity-95" : ""}`}
      />
    </div>
  );
}
